import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../styles/AdminDeposits.css";

const API_BASE_URL = "https://growrise.shop";

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingId, setProcessingId] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Admin access required. Please login again.",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");

    window.location.replace("/login");
  };

  const fetchDeposits = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/deposits/admin/all`, {
        method: "GET",
        headers: getHeaders(),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch deposits");
      }

      setDeposits(Array.isArray(data.deposits) ? data.deposits : []);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load deposits",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
  }, []);

  const filteredDeposits = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return deposits.filter((item) => {
      const matchesSearch =
        !keyword ||
        String(item.id || "").toLowerCase().includes(keyword) ||
        String(item.transactionId || "").toLowerCase().includes(keyword) ||
        String(item.method || "").toLowerCase().includes(keyword) ||
        String(item.user?.name || "").toLowerCase().includes(keyword) ||
        String(item.user?.email || "").toLowerCase().includes(keyword);

      const matchesStatus =
        statusFilter === "all" ||
        String(item.status || "").toLowerCase() === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [deposits, search, statusFilter]);

  const approveDeposit = async (depositId) => {
    try {
      setProcessingId(depositId);

      const response = await fetch(
        `${API_BASE_URL}/api/deposits/admin/${depositId}/approve`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({
            adminNote: "Approved by admin",
          }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to approve deposit");
      }

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text: data.message || "Deposit approved successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchDeposits();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to approve deposit",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setProcessingId("");
    }
  };

  const rejectDeposit = async (depositId) => {
    try {
      setProcessingId(depositId);

      const response = await fetch(
        `${API_BASE_URL}/api/deposits/admin/${depositId}/reject`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({
            adminNote: "Rejected by admin",
          }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to reject deposit");
      }

      await Swal.fire({
        icon: "success",
        title: "Rejected",
        text: data.message || "Deposit rejected successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchDeposits();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to reject deposit",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setProcessingId("");
    }
  };

  const handleApproveClick = async (depositId) => {
    const result = await Swal.fire({
      icon: "question",
      title: "Approve Deposit?",
      text: "User balance add ho jayega.",
      showCancelButton: true,
      confirmButtonText: "Approve",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    if (result.isConfirmed) {
      await approveDeposit(depositId);
    }
  };

  const handleRejectClick = async (depositId) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Reject Deposit?",
      text: "Ye request reject ho jayegi.",
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    if (result.isConfirmed) {
      await rejectDeposit(depositId);
    }
  };

  const getStatusClass = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "approved") return "approved";
    if (normalized === "rejected") return "rejected";
    return "pending";
  };

  return (
    <div className="admin-deposits-page">
      <div className="admin-deposits-wrapper">
        <div className="admin-deposits-topbar">
          <div>
            <h1>Admin Deposits</h1>
            <p>Manual deposit verification and approval panel</p>
          </div>

          <button
            className="admin-deposits-refresh-btn"
            onClick={fetchDeposits}
          >
            Refresh
          </button>
        </div>

        <div className="admin-deposits-stats-grid">
          <div className="admin-deposits-stat-card">
            <span>Total Requests</span>
            <strong>{deposits.length}</strong>
          </div>

          <div className="admin-deposits-stat-card">
            <span>Pending</span>
            <strong>
              {
                deposits.filter(
                  (item) => String(item.status || "").toLowerCase() === "pending"
                ).length
              }
            </strong>
          </div>

          <div className="admin-deposits-stat-card">
            <span>Approved</span>
            <strong>
              {
                deposits.filter(
                  (item) => String(item.status || "").toLowerCase() === "approved"
                ).length
              }
            </strong>
          </div>

          <div className="admin-deposits-stat-card">
            <span>Rejected</span>
            <strong>
              {
                deposits.filter(
                  (item) => String(item.status || "").toLowerCase() === "rejected"
                ).length
              }
            </strong>
          </div>
        </div>

        <div className="admin-deposits-card">
          <div className="admin-deposits-card-top">
            <h2>Deposit Requests</h2>

            <div className="admin-deposits-filters">
              <input
                type="text"
                placeholder="Search by user, email, txid, method"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-deposits-search-input"
              />

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-deposits-filter-select"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="admin-deposits-empty-state">Loading deposits...</div>
          ) : filteredDeposits.length === 0 ? (
            <div className="admin-deposits-empty-state">
              No deposit requests found
            </div>
          ) : (
            <div className="admin-deposits-table-wrap">
              <table className="admin-deposits-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>TXID</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDeposits.map((item) => (
                    <tr key={item.id}>
                      <td>{item.user?.name || "-"}</td>
                      <td>{item.user?.email || "-"}</td>
                      <td>${Number(item.amount || 0).toFixed(2)}</td>
                      <td>{item.method || "-"}</td>
                      <td className="admin-deposits-txid">
                        {item.transactionId || "-"}
                      </td>
                      <td>
                        <span
                          className={`admin-deposits-status ${getStatusClass(
                            item.status
                          )}`}
                        >
                          {item.status || "pending"}
                        </span>
                      </td>
                      <td>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : "-"}
                      </td>
                      <td>
                        <div className="admin-deposits-action-group">
                          <button
                            className="admin-deposits-approve-btn"
                            onClick={() => handleApproveClick(item.id)}
                            disabled={
                              processingId === item.id ||
                              String(item.status || "").toLowerCase() !==
                                "pending"
                            }
                          >
                            {processingId === item.id
                              ? "Processing..."
                              : "Approve"}
                          </button>

                          <button
                            className="admin-deposits-reject-btn"
                            onClick={() => handleRejectClick(item.id)}
                            disabled={
                              processingId === item.id ||
                              String(item.status || "").toLowerCase() !==
                                "pending"
                            }
                          >
                            {processingId === item.id
                              ? "Processing..."
                              : "Reject"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}